'use client';

import { FC } from 'react';
import { Box, Button } from '@mui/material';
import { StandardDialog } from '@/components/common/StandardDialog';
import Recipe from '@/components/recipe/Recipe';
import { RecipeStore } from '@/components/recipe/stores';
import { RecipeState } from '@/components/recipe';

export interface RecipeFullViewDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** MobX store in edit mode. Owned by the inline editor; this dialog only borrows it. */
  store?: RecipeStore;
  /** Plain recipe data in readonly mode. */
  data?: RecipeState;
  readonly: boolean;
}

/**
 * Full-screen popup that shows only the recipe table.
 *
 * Intended for large recipes that are awkward to work with inline in the detail panel.
 * It renders the SAME store/data as the inline editor, so edits stay in sync and there is
 * no separate save: closing the dialog leaves the shared state untouched. `ownsStore={false}`
 * ensures closing the dialog does not dispose the store still used by the inline editor.
 */
export const RecipeFullViewDialog: FC<RecipeFullViewDialogProps> = ({
  open,
  onClose,
  title,
  store,
  data,
  readonly,
}) => {
  if (!open) return null;

  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      title={title}
      fullScreen
      showDefaultActions={false}
      slotProps={{
        paper: { sx: { display: 'flex', flexDirection: 'column' } },
      }}
      sx={{ '& .MuiDialogContent-root': { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } }}
      actions={
        <Button variant="contained" onClick={onClose} sx={{ ml: 'auto' }}>
          Close
        </Button>
      }
    >
      <Box sx={{ mt: 1, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Recipe store={store} data={data} readonly={readonly} ownsStore={false} layout="fullView" />
      </Box>
    </StandardDialog>
  );
};

export default RecipeFullViewDialog;
